/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { UINotification } from '@poweralpha/common';
import { formatNotificationTime, getNotificationContext } from '../display';
import NotificationGlyph from './NotificationGlyph';
import { renderNotificationContent } from './notificationContent';
import {
  getMessageQuoteSx,
  getRowSx,
  lineClampSx,
  ROW_ACTIONS_CLASS,
  ROW_TIME_CLASS,
} from './styles';

interface Props {
  notification: UINotification;
  selected?: boolean;
  onSelect: (notification: UINotification) => void;
  onMarkRead?: (notification: UINotification) => void;
}

function NotificationRow({
  notification,
  selected = false,
  onSelect,
  onMarkRead,
}: Readonly<Props>) {
  const { subject, notif_content, link, message, severity, source, viewed_at } = notification;
  const unread = !viewed_at;

  return (
    <Box
      component="div"
      role="button"
      tabIndex={0}
      aria-current={selected || undefined}
      data-unread={unread || undefined}
      onClick={() => onSelect(notification)}
      onKeyDown={(event) => {
        // Keys on nested controls (Mark as read) belong to them, not to the row.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(notification);
        }
      }}
      sx={getRowSx({ unread, selected })}
    >
      <NotificationGlyph source={source} severity={severity} />
      <Stack spacing={0.375} sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 22 }}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              fontSize: 13,
              fontWeight: unread ? 600 : 500,
              color: unread ? 'text.primary' : 'text.secondary',
            }}
          >
            {subject}
          </Typography>
          <Typography className={ROW_TIME_CLASS} sx={{ fontSize: 11, color: 'text.secondary' }}>
            {formatNotificationTime(notification.created_at)}
          </Typography>
          {unread && onMarkRead && (
            <Box className={ROW_ACTIONS_CLASS}>
              <Tooltip title="Mark as read">
                <IconButton
                  size="small"
                  aria-label="Mark as read"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMarkRead(notification);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <DoneAllRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>
        <Typography
          sx={[
            { fontSize: 12, lineHeight: '17px', color: unread ? 'text.primary' : 'text.secondary' },
            lineClampSx(2),
          ]}
        >
          {renderNotificationContent(notif_content, link)}
        </Typography>
        {message && (
          <Typography noWrap sx={[{ fontSize: 12 }, getMessageQuoteSx(`${severity}.main`)]}>
            {message}
          </Typography>
        )}
        <Typography noWrap sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary' }}>
          {getNotificationContext(notification)}
        </Typography>
      </Stack>
      <Box
        aria-label={unread ? 'Unread' : undefined}
        sx={{
          flexShrink: 0,
          mt: 0.75,
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: unread ? 'primary.main' : 'transparent',
        }}
      />
    </Box>
  );
}

export default NotificationRow;
